import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Typography,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Collapse,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  listFormularios,
  updateFormulario,
  getFormulario,
  duplicarFormulario,
  type Formulario,
} from '../../api/formularios';
import { useSnackbar } from 'notistack';

interface Props {
  onUse?: (template: Formulario) => void;
  onEdit?: (template: Formulario) => void;
  onDuplicate?: (template: Formulario) => void;
  onDelete?: (template: Formulario) => void;
  onActivate?: (template: Formulario) => void;
  onDeactivate?: (template: Formulario) => void;
  defaultOpen?: boolean;
}

export default function FormsPlantillasDropDown(props: Props) {
  const {
    onUse,
    onEdit,
    onDuplicate,
    onDelete,
    onActivate,
    onDeactivate,
    defaultOpen = false,
  } = props;
  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(false);
  const [plantillas, setPlantillas] = useState<Formulario[]>([]);
  const [usingId, setUsingId] = useState<number | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);

  const loadTemplates = async () => {
    let mounted = true;
    setLoading(true);
    try {
      const { data } = await listFormularios({ limit: 200 });
      if (!mounted) return;
      // Excluir evaluaciones y formularios marcados como eliminados
      const found = (data || []).filter(
        (f) => f.esPlantilla === true && !f.esEvaluacion && f.estado !== 'eliminado',
      );
      setPlantillas(found);
    } catch (err) {
      console.error('Error loading templates', err);
      setPlantillas([]);
    } finally {
      if (mounted) setLoading(false);
    }
  };

  useEffect(() => {
    // Load when opened
    if (open) {
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    // Listen for external changes to plantillas and reload immediately
    const handler = () => {
      loadTemplates();
    };
    window.addEventListener('plantillas:changed', handler as EventListener);
    return () => window.removeEventListener('plantillas:changed', handler as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTemplate, setMenuTemplate] = useState<Formulario | null>(null);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, template: Formulario) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuTemplate(template);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuTemplate(null);
  };

  const handleEdit = () => {
    if (menuTemplate) {
      onEdit?.(menuTemplate);
      handleMenuClose();
    }
  };

  const handleDuplicate = async () => {
    if (!menuTemplate) return;
    setDuplicatingId(menuTemplate.id);
    try {
      // Duplicate on server
      const newForm = await duplicarFormulario(menuTemplate.id);
      // Ensure it's marked as plantilla
      const updated = await updateFormulario(newForm.id, { esPlantilla: true });
      // Add immediately to local plantillas list (avoid duplicate ids)
      setPlantillas((prev) => [updated, ...prev.filter((p) => p.id !== updated.id)]);
      enqueueSnackbar('Plantilla duplicada', { variant: 'success' });
      // Notify others
      window.dispatchEvent(new CustomEvent('plantillas:changed', { detail: { id: updated.id } }));
    } catch (err: any) {
      console.error('Error duplicando plantilla', err);
      enqueueSnackbar(err?.response?.data?.message || 'Error al duplicar plantilla', { variant: 'error' });
    } finally {
      setDuplicatingId(null);
      handleMenuClose();
    }
  };

  const handleActivate = () => {
    if (menuTemplate) {
      onActivate?.(menuTemplate);
      handleMenuClose();
    }
  };

  const handleDeactivate = () => {
    if (menuTemplate) {
      onDeactivate?.(menuTemplate);
      handleMenuClose();
    }
  };

  const handleUse = async (template: Formulario) => {
    setUsingId(template.id);
    try {
      const full = await getFormulario(template.id);
      // Call parent with full formulario data
      onUse?.(full);
      setOpen(false);
    } catch (err: any) {
      console.error('Error loading full template', err);
      enqueueSnackbar(err?.response?.data?.message || 'Error al cargar plantilla', {
        variant: 'error',
      });
    } finally {
      setUsingId(null);
    }
  };

  const { enqueueSnackbar } = useSnackbar();

  const handleDelete = async () => {
    if (!menuTemplate) return;
    const ok = window.confirm(
      '¿Estás seguro de eliminar esta plantilla? Esto solo la quitará de la lista de Plantillas; el formulario permanecerá.',
    );
    if (!ok) {
      handleMenuClose();
      return;
    }

    try {
      await updateFormulario(menuTemplate.id, { esPlantilla: false });
      enqueueSnackbar('Plantilla eliminada', { variant: 'success' });
      // Quitar inmediatamente del estado local para actualizar UI sin esperar otra carga
      setPlantillas((prev) => prev.filter((p) => p.id !== menuTemplate.id));
      // Recargar plantillas en background por si hay desincronización
      loadTemplates();
      // Notify others if needed
      window.dispatchEvent(
        new CustomEvent('plantillas:changed', { detail: { id: menuTemplate.id } }),
      );
    } catch (err: any) {
      console.error('Error removing plantilla:', err);
      enqueueSnackbar(err?.response?.data?.message || 'No se pudo eliminar la plantilla', {
        variant: 'error',
      });
    } finally {
      handleMenuClose();
    }
  };

  return (
    <Box>
      <Button
        fullWidth
        onClick={() => setOpen((s) => !s)}
        sx={{
          backgroundColor: '#F7E463',
          color: '#000',
          fontWeight: 'bold',
          borderRadius: '8px',
          py: 1.5,
          '&:hover': { backgroundColor: '#f6e052' },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="center" width="100%">
          <Typography sx={{ fontWeight: 'bold' }}>VER PLANTILLAS</Typography>
          <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
            {open ? (
              <ExpandLessIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
            ) : (
              <ExpandMoreIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
            )}
          </Box>
        </Stack>
      </Button>

      <Collapse in={open} timeout={300} unmountOnExit>
        <Paper elevation={0} sx={{ mt: 2, border: '1px solid #000000', borderRadius: '8px', transformOrigin: 'top', boxShadow: 'none', transition: 'opacity 300ms ease', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Nº</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Fecha de creación</TableCell>
                  <TableCell>Creado por</TableCell>
                  <TableCell>Departamento</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plantillas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Ningún formulario guardado como Plantilla
                    </TableCell>
                  </TableRow>
                ) : (
                  plantillas.map((p, idx) => (
                    <TableRow key={p.id} hover>
                      <TableCell align="center">{idx + 1}</TableCell>
                      <TableCell>{p.titulo}</TableCell>
                      <TableCell>
                        {p.createdAt ? new Date(p.createdAt).toLocaleDateString('es-ES') : ''}
                      </TableCell>
                      <TableCell>{p.creadoPorUsuario?.nombreCompleto ?? ''}</TableCell>
                      <TableCell>{p.departamento ?? '-'}</TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleUse(p)}
                            disabled={usingId !== null}
                            sx={{ backgroundColor: '#21A598', color: '#fff', '&:hover': { backgroundColor: '#1b8a85', } }}>
                            {usingId === p.id ? <CircularProgress size={18} /> : 'USAR'}
                          </Button>
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, p)}>
                            <MoreVertIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}

          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleEdit}>
              <EditIcon sx={{ mr: 1 }} fontSize="small" />
              Editar
            </MenuItem>
            <MenuItem onClick={handleDuplicate}>
              <ContentCopyIcon sx={{ mr: 1 }} fontSize="small" />
              {duplicatingId === menuTemplate?.id ? <CircularProgress size={16} /> : 'Duplicar'}
            </MenuItem>

            {menuTemplate && menuTemplate.estado !== 'activo' && (
              <MenuItem onClick={handleActivate}>
                <PlayArrowIcon sx={{ mr: 1 }} fontSize="small" />
                Activar
              </MenuItem>
            )}

            <MenuItem onClick={handleDelete}>
              <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
              Eliminar
            </MenuItem>
          </Menu>
        </Paper>
      </Collapse>
    </Box>
  );
}
